import { FC } from 'react';
import { Row, Col } from 'antd';
import Button from '../../button/Button';
import SaveIcon from '../../../assets/icons/SaveIcon';
import ArrowIcon from '../../../assets/icons/ArrowIcon';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { useLocation } from 'react-router-dom';
import { ROUTER_PATHS } from '../../../navigation/routerPaths';
import {
    saveAsDraftMyInformation,
    saveAsPublishMyInformation,
} from '../../../redux/reducers/onboarding/tabs/myInformationAsyncThunk';
import { historyApi } from '../../../common/api/services/HistoryApi/historyApi';
import { OnboardingRequestStatuses } from '../../../common/api/services/OnboardingApi/enums/OnboardingRequestStatuses';
import { TabKeyByPath } from '../../../common/api/services/OnboardingApi/enums/OnboardingRequestKeys';
import { setAllFieldsWC } from '../../../redux/reducers/onboarding-history-WC/sliceReducer';
import {
    setActiveKeys,
    setAllFields,
    setHistoryErrorMessages,
} from '../../../redux/reducers/onboarding-history/sliceReducer';
import { FieldKeys } from '../../../common/api/services/HistoryApi/types/fieldKeys';
import { openErrorNotification } from '../../notification/Notification';

const { workPermit, bankAccount, avsAhv, personalInformation, workContract } = ROUTER_PATHS;

const MyInformationFooter: FC = () => {
    const pathName = useLocation().pathname;
    const dispatch = useAppDispatch();
    const isPageHasHistory = [personalInformation, workContract, bankAccount, workPermit].some((path) =>
        pathName.includes(path)
    );
    const activeFieldsOnboarding = useAppSelector((state) => state.onBoardingHistory.activeFields);
    const historyFieldsOnboarding = useAppSelector((state) => state.onBoardingHistory.historyFields);
    const historyFieldsWC = useAppSelector((state) => state.onBoardingHistoryWC.historyFields);
    const hasWorkPermit = useAppSelector((state) => state.onboarding.workPermit.hasWorkPermit);
    const hasBankAccount = useAppSelector((state) => state.onboarding.bankAccount.hasBankAccount);
    const hasSsn = useAppSelector((state) => state.onboarding.ssnInformation.hasSSN);
    const activeFieldsWC = useAppSelector((state) => state.onBoardingHistoryWC.activeFields);
    const isWorkContractPage = pathName.includes(workContract);

    const activeFields = isWorkContractPage ? activeFieldsWC : activeFieldsOnboarding;
    const { hasSwissAddress, hasAbroadAddress, hasOtherJob, hasCBA, hasTreasury } = useAppSelector(
        (state) => state.onboarding.personalInfo
    );
    const personalInfo = useAppSelector((state) => state.onboarding.personalInfo);
    const { workForm, nationality } = personalInfo;
    const currentPage = [personalInformation, workContract, bankAccount, workPermit].find((path) =>
        pathName.includes(path)
    ) as keyof typeof TabKeyByPath;
    const isSwiss = nationality === 'Switzerland';
    const isWorkPermitSwiss = pathName.includes(workPermit) && isSwiss;
    const historyFields = isWorkContractPage ? historyFieldsWC : historyFieldsOnboarding;

    const combineFields = () => {
        const result: Record<string, Array<Record<string, unknown>>> = {};

        Object.entries(activeFields).forEach(([key, value]) => {
            if (!result[key]) result[key] = [];
            result[key] = result[key].concat(
                value.map((item) => ({
                    ...item,
                    isActive: true,
                }))
            );
        });

        Object.entries(historyFields).forEach(([key, value]) => {
            if (!result[key]) result[key] = [];
            result[key] = result[key].concat(
                value.map((item) => ({
                    ...item,
                    isActive: false,
                }))
            );
        });

        //NOTE: remove field with spesial conditions

        //PI
        if (
            !personalInfo.hasSwissAddress &&
            (activeFields[FieldKeys.swissAddress] || historyFields[FieldKeys.swissAddress])
        ) {
            result[FieldKeys.swissAddress] = [];
        }

        if (
            !personalInfo.hasAbroadAddress &&
            (activeFields[FieldKeys.abroadAddress] || historyFields[FieldKeys.abroadAddress])
        ) {
            result[FieldKeys.abroadAddress] = [];
        }
        if (!personalInfo.hasCBA && (activeFields[FieldKeys.cba] || historyFields[FieldKeys.cba])) {
            result[FieldKeys.cba] = [];
        }

        if (result[FieldKeys.maritalStatus]) {
            result[FieldKeys.maritalStatus] = result[FieldKeys.maritalStatus].map((item) => {
                if (item.maritalStatus !== 'Married') {
                    return {
                        ...item,
                        maritalStatusDetails: {},
                    };
                }
                return item;
            });
        }

        if (!personalInfo.hasOtherJob && (activeFields[FieldKeys.otherJob] || historyFields[FieldKeys.otherJob])) {
            result[FieldKeys.otherJob] = [];
        }

        if (!personalInfo.hasTreasury && (activeFields[FieldKeys.treasury] || historyFields[FieldKeys.treasury])) {
            result[FieldKeys.treasury] = [];
        }

        return result;
    };
    const openPITabs = () => {
        if (pathName.includes(personalInformation)) {
            dispatch(setActiveKeys(['1', '2', '3', '4', '5']));
        }
    };
    const saveAsPublishHandler = (): void => {
        openPITabs();
        if (isPageHasHistory && !isWorkPermitSwiss) {
            historyApi
                .updateHistory({
                    status: OnboardingRequestStatuses.published,
                    id: 'consultant',
                    matchedKeys: combineFields(),
                    tab: TabKeyByPath[currentPage],
                    options: { hasAbroadAddress, hasSwissAddress, hasOtherJob, hasCBA, hasTreasury },
                })
                .then((resp) => {
                    if (currentPage === workContract) {
                        dispatch(setAllFieldsWC(resp));
                    } else {
                        dispatch(setAllFields(resp));
                    }
                    dispatch(setHistoryErrorMessages({ messages: [], tabName: TabKeyByPath[currentPage] }));
                })
                .then(() => {
                    dispatch(saveAsPublishMyInformation());
                })
                .catch((e: any) => {
                    const message = e?.response?.data?.message;
                    if (message) {
                        openErrorNotification(message);
                        if (message.toLowerCase().includes('swiss') || message.toLowerCase().includes('abroad')) {
                            dispatch(setActiveKeys(['1']));
                        }
                    }
                    dispatch(
                        setHistoryErrorMessages({
                            messages: e?.response?.data?.messages,
                            tabName: TabKeyByPath[currentPage],
                        })
                    );
                });
        } else {
            dispatch(saveAsPublishMyInformation());
        }
    };

    const saveAsDraftHandler = (): void => {
        if (isPageHasHistory) {
            historyApi
                .updateHistory({
                    status: OnboardingRequestStatuses.draft,
                    id: 'consultant',
                    matchedKeys: combineFields(),
                    tab: TabKeyByPath[currentPage],
                    options: { hasAbroadAddress, hasSwissAddress, hasOtherJob, hasCBA, hasTreasury },
                })
                .then((resp) => {
                    if (currentPage === workContract) {
                        dispatch(setAllFieldsWC(resp));
                    } else {
                        dispatch(setAllFields(resp));
                    }
                    dispatch(setHistoryErrorMessages({ messages: [], tabName: TabKeyByPath[currentPage] }));
                })
                .then(() => {
                    dispatch(saveAsDraftMyInformation());
                })
                .catch((e: any) => {
                    dispatch(
                        setHistoryErrorMessages({
                            messages: e?.response?.data?.messages,
                            tabName: TabKeyByPath[currentPage],
                        })
                    );
                });
        } else {
            dispatch(saveAsDraftMyInformation());
        }
    };

    const isSkipConditions =
        (pathName.includes(workPermit) && !hasWorkPermit) ||
        pathName.includes(bankAccount) ||
        (pathName.includes(avsAhv) && !hasSsn);

    const buttonText = isSkipConditions ? 'Skip and go next' : 'Save and publish';

    return (
        <Row align="middle" className="my-information-footer" justify="space-between">
            <Col span={24}>
                <Row gutter={[16, 0]} justify="end">
                    {!isSkipConditions && (
                        <Col>
                            <Button
                                buttonStyle="secondary"
                                buttonSize="normal"
                                leftIcon={<SaveIcon />}
                                onClick={saveAsDraftHandler}
                            >
                                Save as Draft
                            </Button>
                        </Col>
                    )}
                    <Col>
                        <Button
                            buttonStyle="primary"
                            buttonSize="normal"
                            rightIcon={<ArrowIcon />}
                            onClick={saveAsPublishHandler}
                        >
                            {buttonText}
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default MyInformationFooter;
